import { useCallback, useMemo, useState, type FormEvent } from 'react';
import { Alert } from '@/components/alert';
import { AuthCard } from '@/components/auth-card';
import { Button } from '@/components/button';
import { PageFooter } from '@/components/page-footer';
import { TextField } from '@/components/text-field';
import { env } from '@/lib/env';
import { InteractionApiError } from '@/lib/interaction-api';
import { useLocale } from '@/lib/locale-context';
import { copyFor, messageFor } from '@/lib/messages';
import { createRecoveryApi } from '@/lib/recovery-api';
import { useRecoveryCsrf } from './use-recovery-csrf';
import { BackToSignIn } from './back-to-sign-in';

/**
 * Asks for the address and always says the same thing afterwards — whether or
 * not anyone holds it (docs/auth/40-features/password-reset.md AC1). The
 * confirmation copy is worded so that it stays true either way.
 */
export function ForgotPassword() {
  const { locale } = useLocale();
  const copy = useMemo(() => copyFor(locale), [locale]);
  const api = useMemo(() => createRecoveryApi(env.authApiUrl), []);
  const { csrfToken, resetTtlMinutes, bootstrapError, retryBootstrap } =
    useRecoveryCsrf(api);
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const submit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      const address = email.trim();
      if (!address || !csrfToken) return;
      setPending(true);
      setErrorCode(null);
      try {
        await api.start(address, csrfToken, locale);
        setSent(true);
      } catch (error) {
        setErrorCode(
          error instanceof InteractionApiError ? error.code : 'unknown',
        );
      } finally {
        setPending(false);
      }
    },
    [api, csrfToken, email, locale],
  );

  const footer = <PageFooter />;

  if (sent) {
    return (
      <AuthCard title={copy.forgotSentTitle} footer={footer}>
        {/* role="status": the card swaps in place, and without an
            announcement a screen-reader user hears nothing after pressing
            send — concludes it failed, and resubmits into the rate limit.
            break-words: the interpolated address is one unbreakable token. */}
        <p
          role="status"
          className="break-words text-sm leading-relaxed text-buds-muted"
        >
          {copy.forgotSentBody(email.trim(), resetTtlMinutes)}
        </p>
        <BackToSignIn label={copy.backToSignIn} />
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title={copy.forgotTitle}
      subtitle={copy.forgotSubtitle}
      footer={footer}
    >
      <form onSubmit={(e) => void submit(e)} noValidate className="space-y-5">
        {bootstrapError ? (
          <div className="space-y-3">
            <Alert tone="error">{messageFor(bootstrapError, locale)}</Alert>
            <Button type="button" variant="outline" onClick={retryBootstrap}>
              {copy.errorRetry}
            </Button>
          </div>
        ) : null}
        {errorCode ? (
          <Alert tone="error">{messageFor(errorCode, locale)}</Alert>
        ) : null}
        <TextField
          id="recovery-email"
          label={copy.emailLabel}
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="email"
          inputMode="email"
          autoFocus
          required
        />
        {/* Disabled until the CSRF token is in hand: a submit that silently
            early-returns is a button that does nothing, which reads as the
            page being broken — because it is. */}
        <Button
          type="submit"
          loading={pending}
          disabled={!email.trim() || !csrfToken}
        >
          {copy.forgotSubmit}
        </Button>
      </form>
      <BackToSignIn label={copy.backToSignIn} />
    </AuthCard>
  );
}
